import React, { useEffect, useRef } from "react";
import {
  HeadingText,
  ImageMessage,
  Modal,
  VisuallyHidden,
  type GDSHeadingLevel,
} from "@leagueplatform/genesis-core";
import { BaseModalContainer } from "components/modals/common/base-modal.component";

interface Props {
  image: string;
  title: string;
  titleLevel?: GDSHeadingLevel;
  footer: React.ReactNode;
  children: React.ReactNode;
  centerTextOnMobile?: boolean;
  fullHeightOnMobile?: boolean;
  autoFocusTitle?: boolean;
}

export const ImageMessageModalContent = ({
  image,
  title,
  titleLevel = "2",
  footer,
  children,
  centerTextOnMobile = false,
  fullHeightOnMobile = false,
  autoFocusTitle = false,
}: Props) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!autoFocusTitle) return undefined;
    const id = window.setTimeout(() => headingRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [autoFocusTitle]);

  return (
    <BaseModalContainer footer={footer} fullHeightOnMobile={fullHeightOnMobile}>
      <VisuallyHidden>
        <Modal.Title>{title}</Modal.Title>
      </VisuallyHidden>
      <ImageMessage
        image={image}
        css={{
          flexGrow: 1,
          padding: "$two $none",
          textAlign: "left",
          "@mobile": {
            padding: "$half",
            ...(centerTextOnMobile && { textAlign: "center" }),
          },
        }}
      >
        <HeadingText
          ref={headingRef}
          tabIndex={autoFocusTitle ? -1 : undefined}
          level={titleLevel}
          size="lg"
          css={{
            paddingBlock: "$one",
            textAlign: "left",
            outline: "none",
            ...(centerTextOnMobile && { "@mobile": { textAlign: "center" } }),
          }}
        >
          {title}
        </HeadingText>
        {children}
      </ImageMessage>
    </BaseModalContainer>
  );
};
